import { config } from '@/config/config.js'
import { Lesson, Lessons } from '@/models/Lesson'
import { Schedules } from '@/models/Schedule'
import { Name } from '@/models/Name'
import { Address } from '@/models/Address'

class Teacher {
  constructor(
    values
  ) {
    if (!(typeof values.uuid === 'string')) {
      throw new TypeError(`The type of 'uuid' must be String. Actual: ${typeof values.uuid}`)
    }
    if (!(values.name instanceof Name)) {
      throw new TypeError(`'Name' must be an instance of [object Name]. Actual: ${typeof values.Name}`)
    }
    if (!(typeof values.email === 'string')) {
      throw new TypeError(`The type of 'email' must be String. Actual: ${typeof values.email}`)
    }
    if (!(values.address instanceof Address)) {
      throw new TypeError(`'address' must be an instance of [object Address]. Actual: ${typeof values.address}`)
    }
    if (!(values.birthday instanceof Date)) {
      throw new TypeError(`'birthday' must be an instance of [object Date]. Actual: ${typeof values.birthday}`)
    }
    if (!(typeof values.sex === 'string')) {
      throw new TypeError(`The type of 'sex' must be String. Actual: ${typeof values.sex}`)
    }
    if (!(typeof values.phone === 'string')) {
      throw new TypeError(`The type of 'phone' must be String. Actual: ${typeof values.phone}`)
    }
    if (!(values.availableSchedules instanceof Schedules)) {
      throw new TypeError(`'availableSchedules' must be an instance of [object Schedules]. Actual: ${typeof values.availableSchedules}`)
    }
    if (!(typeof values.expectedWage === 'number')) {
      throw new TypeError(`The type of 'expectedWage' must be Number. Actual: ${typeof values.expectedWage}`)
    }
    if (!(typeof values.nearestStation === 'string')) {
      throw new TypeError(`The type of 'nearestStation' must be String. Actual: ${typeof values.nearestStation}`)
    }
    if (!(typeof values.university === 'string')) {
      throw new TypeError(`The type of 'university' must be String. Actual: ${typeof values.university}`)
    }
    if (!(typeof values.inUniversity === 'boolean')) {
      throw new TypeError(`The type of 'inUniversity' must be Boolean. Actual: ${typeof values.inUniversity}`)
    }
    if (!(typeof values.selfIntroduction === 'string')) {
      throw new TypeError(`The type of 'selfIntroduction' must be String. Actual: ${typeof values.selfIntroduction}`)
    }
    if (!(values.studentIds instanceof Array)) {
      throw new TypeError(`'studentIds' must be an instance of [object Array]. Actual: ${typeof values.studentIds}`)
    }
    if (!(values.lessons instanceof Lessons)) {
      throw new TypeError(`'lessons' must be an instance of [object Lessons]. Actual: ${typeof values.lessons}`)
    }
    if (!(typeof values.active === 'boolean')) {
      throw new TypeError(`The type of 'active' must be Boolean. Actual: ${typeof values.active}`)
    }
    this.uuid = values.uuid
    this.name = values.name
    this.email = values.email
    this.address = values.address
    this.birthday = values.birthday
    this.sex = values.sex
    this.phone = values.phone
    this.availableSchedules = values.availableSchedules
    this.expectedWage = values.expectedWage
    this.nearestStation = values.nearestStation
    this.university = values.university
    this.inUniversity = values.inUniversity
    this.selfIntroduction = values.selfIntroduction
    this.studentIds = values.studentIds
    this.lessons = values.lessons
    this.createdAt = values.createdAt
    this.updatedAt = values.updatedAt
    if (values.iconUrl === undefined) {
      this.iconUrl = config.DEFAULT_ICON_PATH
    } else {
      this.iconUrl = values.iconUrl
    }
    this.active = values.active
  }

  toObject() {
    return {
      id: this.uuid,
      data: {
        personalName: this.name.personalName,
        familyName: this.name.familyName,
        flatPersonalName: this.name.flatPersonalName,
        flatFamilyName: this.name.flatFamilyName,
        email: this.email,
        postalCode: this.address.postalCode,
        prefecture: this.address.prefecture,
        municipality: this.address.municipality,
        subAddress: this.address.subAddress,
        birthday: this.birthday,
        sex: this.sex,
        phone: this.phone,
        availableSchedules: this.availableSchedules.toObjects(),
        expectedWage: this.expectedWage,
        nearestStation: this.nearestStation,
        university: this.university,
        inUniversity: this.inUniversity,
        selfIntroduction: this.selfIntroduction,
        studentIds: this.studentIds,
        lessons: this.lessons.toObjects(),
        iconUrl: this.iconUrl,
        createdAt: this.createdAt,
        updatedAt: this.updatedAt,
        active: this.active
      }
    }
  }

  getId() {
    return this.uuid
  }

  isActive() {
    return this.active
  }

  unsubscribe() {
    this.active = false
    return this
  }

  fullAddress() {
    return "〒" + this.address.postalCode + " " + this.address.fullAddress()
  }

  fullName() {
    return this.name.fullName()
  }

  flatFullName() {
    return this.name.flatFullName()
  }

  birthdayToString() {
    const t = this.birthday.getFullYear()
    const m = this.birthday.getMonth() + 1
    const d = this.birthday.getDate()
    return t + "-" + m + "-" + d
  }

  availableSchedulesToStrings() {
    return this.availableSchedules.toStrings()
  }

  inUniversityToString() {
    return (this.inUniversity) ? '在学' : '既卒'
  }

  universityInfo() {
    return this.university + ': ' + this.inUniversityToString()
  }

  genderInJapanese() {
    return (this.isMale()) ? "男性" : "女性"
  }

  isMale () {
    return (this.sex === "male")
  }

  isGender(gender) {
    if (!(typeof gender === "string")) {
      throw new TypeError(`The type of gender must be String. Actual: ${typeof gender}`)
    }
    return (gender === this.sex)
  }

  inPrefecture(prefecture) {
    if (!(typeof prefecture === "string")) {
      throw new TypeError(`The type of 'prefecture' must be String. Actual: ${typeof prefecture}`)
    }
    return (this.address.hasPrefecture(prefecture))

  }

  containsDaysInAvailableSchedules(days) {
    if (!(days instanceof Array)) {
      throw new TypeError(`'days' must be an instance of Array. Actual: ${typeof days}`)
    }
    return days.some(day => this.availableSchedules.hasDayInAny(day))
  }

  hasId(id) {
    if (!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    return (this.uuid === id) ? true : false
  }

  hasStudentId(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    return (this.studentIds.includes(studentId)) ? true : false
  }

  getStudentIds() {
    return (this.studentIds)
  }

  registerStudentId(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    if (this.studentIds.includes(studentId)) {
      return this
    } else {
      this.studentIds.push(studentId)
      return this
    }
  }

  removeStudentId(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be string. Actual: ${typeof studentId}`)
    }
    this.studentIds.splice(this.studentIds.findIndex(id => {
      return id === studentId
    }), 1)
    return this
  }

  getLesson(lessonId) {
    if (!(typeof lessonId === 'string')) {
      throw new TypeError(`The type of 'lessonId' must be String. Actual: ${typeof lessonId}`)
    }
    return this.lessons.find(lessonId)
  }

  getLessons() {
    return this.lessons
  }


  registerLesson(lesson) {
    if (!(lesson instanceof Lesson)) {
      throw new TypeError(`'lesson' must be an instance of Lesson. Actual: ${typeof lesson}`)
    }
    this.lessons.register(lesson)
    return this
  }

  deleteLesson(id) {
    if (!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be an String. Actual: ${typeof id}`)
    }
    this.lessons.get().splice(this.lessons.get().findIndex(lesson => {
      return id === lesson.id
    }), 1)
    return this
  }
}

class Teachers {
  constructor(teachers) {
    if (!(teachers instanceof Array)) {
      throw new TypeError(`'teachers' must be an instance of [object Array]. Actual: ${typeof teachers}`)
    }
    this.teachers = teachers
  }

  get() {
    return this.teachers
  }

  length() {
    return this.teachers.length
  }

  toObjects() {
    return this.teachers.map(teacher => {
      return teacher.toObject()
    })
  }

  find(teacherId) {
    if (!(typeof teacherId === "string")) {
      throw new TypeError(`The type of 'teacherId' must be String. Actual: ${typeof teacherId}`)
    }
    return this.teachers[this.teachers.findIndex(teacher => teacher.uuid === teacherId)]
  }

  filterByStudentId(studentId) {
    if (!(typeof studentId === 'string')) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    return new Teachers(this.teachers.filter(teacher => teacher.hasStudentId(studentId)))
  }

  filterByConditions(conditions) {
    if (!(conditions instanceof Object)) {
      throw new TypeError(`'conditions' must be an instance of [object Object]. Actual: ${typeof conditions}`)
    }
    const teacherConditions = new TeacherConditions(conditions)
    return new Teachers(this.teachers.filter(teacher => {
      return teacherConditions.filter(teacher)
    }))
  }

  remove(teacher) {
    if (!(teacher instanceof Teacher)) {
      throw new TypeError(`'teacher' must be an instance of [object Teacher]. Actual: ${typeof teacher}`)
    }
    this.teachers.splice(this.teachers.findIndex(t => t.hasId(teacher.uuid)), 1)
    return this
  }

  async asyncMap(callback) {
    return new Teachers(await Promise.all(this.teachers.map(async teacher => await callback(teacher))))
  }

}

class TeacherConditions {
  constructor(conditions) {
    this.gender = conditions.gender
    this.prefecture = conditions.prefecture
    this.availableDays = conditions.availableDays
  }

  filter(teacher) {
    if (!(teacher instanceof Teacher)) {
      throw new TypeError(`'teacher' must be an instance of [object Teacher]. Actual: ${typeof teacher}`)
    }
    if (
      this._isGender(teacher) &&
        this._inPrefecture(teacher) &&
        this._containsDaysInAvailableSchedules(teacher)
    ) {
      return true
    } else {
      return false
    }
  }

  _isGender(teacher) {
    return (!this.gender) ? true : teacher.isGender(this.gender)
  }

  _inPrefecture(teacher) {
    return (!this.prefecture) ? true : teacher.inPrefecture(this.prefecture)
  }

  _containsDaysInAvailableSchedules(teacher) {
    return teacher.containsDaysInAvailableSchedules(this.availableDays)
  }
}

export { Teacher, Teachers }


